import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';
import { Button } from '@/shared/components/ui/Button';
import { PageHeader } from '@/shared/components/ui/PageHeader';
import { StatCard, StatGrid } from '@/shared/components/ui/StatCard';
import { Input } from '@/shared/components/ui/Input';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/ui/Tabs';
import { ModuleGuide } from '@/shared/components/ui/ModuleGuide';
import { ClientProspectTable } from '../components/ClientProspectTable';
import { ClientProspectCard, ClientProspect } from '../components/ClientProspectCard';
import { Search, Plus, Building2, LayoutGrid, List, Users, Briefcase, UserPlus } from 'lucide-react';
import { Spinner } from '@/shared/components/ui/Spinner';
import { AddClientModal } from '../components/AddClientModal';

interface ClientsProspectsResponse {
  items: ClientProspect[];
  total: number;
  limit: number;
  offset: number;
}

// Gradient icon-chip treatment shared with the dashboard StatCard widget
// (frontend/src/features/dashboard/infrastructure/widgets/StatCard.tsx) —
// same tokens (success/warning/danger/info/primary), no new colors.
export function ClientsProspectsPage() {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [tab, setTab] = useState<'active' | 'archived'>('active');
  const [viewMode, setViewMode] = useState<'table' | 'grid'>('table');
  const [page, setPage] = useState(1);
  const [showAddModal, setShowAddModal] = useState(false);
  const limit = 20;

  // Simple debounce for search
  React.useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [search]);

  const { data, isLoading, isError, error } = useQuery<ClientsProspectsResponse>({
    queryKey: ['clients-prospects', { search: debouncedSearch, tab, limit, offset: (page - 1) * limit }],
    queryFn: async () => {
      const params = new URLSearchParams({
        search: debouncedSearch,
        tab,
        limit: String(limit),
        offset: String((page - 1) * limit)
      });
      const response = await api.get(`/api/sales-ops/clients-prospects?${params.toString()}`);
      return response.data;
    }
  });

  const totalPages = data ? Math.ceil(data.total / limit) : 0;

  // Summary stats from the response already fetched by useQuery above — no
  // new API calls. `total` is the real backend count for the active tab;
  // the client/prospect split reflects the currently loaded page of items.
  const items = data?.items || [];
  const clientCount = useMemo(() => items.filter((i) => i.type === 'client').length, [items]);
  const prospectCount = useMemo(() => items.filter((i) => i.type === 'prospect').length, [items]);

  return (
    <>
      <div className="mx-auto max-w-[1580px] space-y-5 pb-16">
      <ModuleGuide 
        title="Clients & Prospects" 
        summary="Manage your complete pipeline of prospects and active clients." 
        steps={['View active or archived clients', 'Add new prospects', 'Export your lists']} 
      />
      
      <PageHeader
        icon={Building2}
        eyebrow="Sales"
        title="Client Profiles"
        description="Active clients and prospects, each with its full account history."
        actions={
          <Button onClick={() => setShowAddModal(true)}>
            <Plus className="h-4 w-4" /> Add New
          </Button>
        }
      />

      <StatGrid cols={3}>
        <StatCard label={tab === 'active' ? 'Active Records' : 'Archived Records'} value={data?.total ?? 0} hint="Total in this tab" icon={Users} tone="primary" />
        <StatCard label="Clients" value={clientCount} hint="On this page" icon={Briefcase} tone="success" />
        <StatCard label="Prospects" value={prospectCount} hint="On this page" icon={UserPlus} tone="info" />
      </StatGrid>

      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 bg-white p-2 rounded-lg border border-gray-200">
        <Tabs value={tab} onValueChange={(t: any) => { setTab(t as 'active' | 'archived'); setPage(1); }}>
          <TabsList className="border-0 mb-0 pb-0">
            <TabsTrigger value="active">Active Records</TabsTrigger>
            <TabsTrigger value="archived">Archived</TabsTrigger>
          </TabsList>
        </Tabs>
        
        <div className="flex items-center gap-3 w-full sm:w-auto">
          <div className="relative w-full sm:w-64">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input 
              placeholder="Search by name, email, phone..." 
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 w-full"
            />
          </div>
          
          <div className="flex items-center bg-gray-100 rounded-md p-1 border border-gray-200">
            <button 
              onClick={() => setViewMode('table')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'table' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <List className="w-4 h-4" />
            </button>
            <button 
              onClick={() => setViewMode('grid')}
              className={`p-1.5 rounded transition-colors ${viewMode === 'grid' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-500 hover:text-gray-700'}`}
            >
              <LayoutGrid className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="h-64 flex items-center justify-center">
          <Spinner className="w-8 h-8 text-blue-500" />
        </div>
      ) : isError ? (
        <div className="bg-red-50 text-red-600 p-4 rounded-lg border border-red-100 text-center">
          Failed to load clients and prospects. {String(error)}
        </div>
      ) : (
        <>
          {viewMode === 'table' ? (
            <ClientProspectTable data={data?.items || []} />
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {data?.items.map(item => (
                <ClientProspectCard key={item.id} data={item} />
              ))}
              {data?.items.length === 0 && (
                <div className="col-span-full py-12 text-center text-gray-500 bg-white rounded-lg border border-gray-200">
                  No records found matching your criteria.
                </div>
              )}
            </div>
          )}

          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4">
              <span className="text-sm text-gray-500">
                Showing {((page - 1) * limit) + 1} to {Math.min(page * limit, data?.total || 0)} of {data?.total || 0} results
              </span>
              <div className="flex items-center gap-2">
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  Previous
                </Button>
                <div className="text-sm font-medium px-4">
                  Page {page} of {totalPages}
                </div>
                <Button 
                  variant="outline" 
                  size="sm" 
                  disabled={page === totalPages}
                  onClick={() => setPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}
    </div>

      {showAddModal && <AddClientModal onClose={() => setShowAddModal(false)} />}
    </>
  );
}
